import { nowUtcIso } from '@/lib/dates'
import { getSupabase } from '@/lib/supabase'
import { openWorkspaceDb, type WorkspaceDb } from '@/offline/db'

export interface ChangeRow {
  workspace_id: string
  server_sequence: number
  entity_type: string
  entity_id: string
  revision: number
  op: string
  payload: Record<string, unknown>
  created_at: string
}

const BATCH = 200

/**
 * Apply server change-log rows to local Dexie in sequence order (upsert by id).
 * Payloads are full entity rows, so a put() reconciles local state to the server's
 * authoritative version. Deletions arrive as rows whose payload carries deleted_at
 * (tombstones); we keep them (soft) so history is preserved.
 */
export async function applyChangeRows(db: WorkspaceDb, rows: ChangeRow[]): Promise<number> {
  let applied = 0
  for (const row of rows) {
    const table = tableFor(db, row.entity_type)
    if (!table) continue
    await table.put(row.payload as never)
    applied++
  }
  return applied
}

function tableFor(db: WorkspaceDb, entityType: string) {
  switch (entityType) {
    case 'device':
      return db.devices
    case 'sale':
      return db.sales
    case 'payment':
      return db.payments
    case 'contact':
      return db.contacts
    case 'expense':
      return db.expenses
    case 'category':
      return db.categories
    default:
      return null
  }
}

/**
 * Pull changes from the server since the stored cursor, in server_sequence order,
 * applying them to Dexie and advancing the cursor. Loops until drained.
 */
export async function pullChanges(workspaceId: string): Promise<{ pulled: number; cursor: number }> {
  const db = openWorkspaceDb(workspaceId)
  let cursor = (await db.pull_cursor.get(workspaceId))?.last_server_sequence ?? 0
  let pulled = 0

  for (;;) {
    const { data, error } = await getSupabase()
      .from('change_log')
      .select('*')
      .eq('workspace_id', workspaceId)
      .gt('server_sequence', cursor)
      .order('server_sequence', { ascending: true })
      .limit(BATCH)
    if (error) throw error

    const rows = (data ?? []) as ChangeRow[]
    if (rows.length === 0) break

    await applyChangeRows(db, rows)
    cursor = rows[rows.length - 1].server_sequence
    await db.pull_cursor.put({
      workspace_id: workspaceId,
      last_server_sequence: cursor,
      updated_at: nowUtcIso(),
    })
    pulled += rows.length
    if (rows.length < BATCH) break
  }

  return { pulled, cursor }
}
